import { createContext, useState, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Цветовые схемы
const lightThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#3F51B5', // Более насыщенный индиго для лучшей видимости
      dark: '#303F9F', 
      light: '#7986CB',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00897B', // Более темный оттенок бирюзового
      dark: '#00695C',
      light: '#4DB6AC',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F8F9FA', // Светло-серый фон
      paper: '#ffffff', // Белый фон карточек
    },
    text: {
      primary: '#212121', // Очень темно-серый для основного текста
      secondary: '#424242', // Темно-серый для вторичного текста
    },
    divider: 'rgba(0, 0, 0, 0.12)', // Более заметный разделитель
    action: {
      active: 'rgba(0, 0, 0, 0.7)', // Более темный для действий
      hover: 'rgba(0, 0, 0, 0.05)',
      selected: 'rgba(63, 81, 181, 0.16)', // Индиго для выбранных элементов
      disabled: 'rgba(0, 0, 0, 0.3)', // Более темный для отключенных элементов
    }
  },
};

const darkThemeOptions = {
  palette: {
    mode: 'dark',
    primary: {
      main: '#5C6BC0',
    },
    secondary: {
      main: '#26A69A',
    },
    background: {
      default: '#121212',
      paper: '#1E1E1E',
    },
    text: {
      primary: '#ffffff',
      secondary: '#bbbbbb',
    }
  },
};

// Общие настройки темы
const commonOptions = {
  typography: {
    fontFamily: '"Roboto", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
    },
    body2: {
      fontSize: '0.875rem',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 30,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: '0 3px 5px rgba(0, 0, 0, 0.2)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
        colorDefault: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
          color: theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.text.primary,
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : theme.palette.background.paper,
          borderRight: theme.palette.mode === 'light' ? '1px solid rgba(0, 0, 0, 0.12)' : '1px solid rgba(255, 255, 255, 0.12)',
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 16,
          backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
        }),
        outlined: ({ theme }) => ({
          borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.05)',
          borderRadius: 8,
        }),
      },
    },
  },
  // Основные настройки пространства
  spacing: 8,
};

// Контекст темы
export const ThemeContext = createContext({
  toggleTheme: () => {},
  mode: 'dark',
  theme: {}
});

// Используем Material-UI ThemeProvider и CssBaseline

export const ThemeProvider = ({ children }) => {
  // Проверяем localStorage при инициализации
  const [mode, setMode] = useState(() => {
    const savedTheme = localStorage.getItem('portfolioTheme');
    return savedTheme || 'dark';
  });
  
  // Создаем тему Material UI
  const themeOptions = mode === 'dark' ? darkThemeOptions : lightThemeOptions;
  const theme = useMemo(
    () => createTheme({
      ...commonOptions,
      ...themeOptions,
      components: {
        ...commonOptions.components
      }
    }),
    [themeOptions] // Удалена ненужная зависимость mode, т.к. themeOptions уже зависит от mode
  );

  // Функция переключения темы
  const toggleTheme = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    setMode(newMode);
    localStorage.setItem('portfolioTheme', newMode);
  };

  // Значение контекста
  const value = {
    toggleTheme,
    mode,
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export { lightThemeOptions, darkThemeOptions, commonOptions };
